import type { EventInput } from "@fullcalendar/react";
import type { CalendarEventResponse } from "calendar/_schemas/calendar-event-schema";
import {
  calendarEventFieldLimits,
  calendarWeekdayAbbreviationLength,
} from "calendar/calendar-event-fields";
import { localDateInputLength, toLocalDateInput } from "_shared/dates/date-input";

export const calendarEventColors = {
  custom: "#1976d2",
  proposal: "#ed6c02",
  workAgreement: "#2e7d32",
  unknown: "#616161",
} as const;

const calendarEventTextColor = "#ffffff";

export type CalendarEventKind = keyof typeof calendarEventColors;

export const calendarEventKindLabels: Record<CalendarEventKind, string> = {
  custom: "Custom event",
  proposal: "Proposal",
  workAgreement: "Work Agreement",
  unknown: "Other managed event",
};

export function getCalendarEventKind(event: Pick<CalendarEventResponse, "source">): CalendarEventKind {
  const resourceType = event.source?.resourceType.toLowerCase();
  if (!resourceType) return "custom";
  if (resourceType === "proposal") return "proposal";
  if (resourceType === "workagreement" || resourceType === "work agreement") return "workAgreement";
  return "unknown";
}

export function getCalendarEventOccurrenceKey(event: Pick<CalendarEventResponse, "id" | "start">): string {
  return `${event.id}:${String(event.start)}`;
}

export function getCalendarEventDateKey(event: Pick<CalendarEventResponse, "isAllDay" | "start">): string {
  if (event.isAllDay) return String(event.start).slice(0, localDateInputLength);
  return toLocalDateInput(new Date(String(event.start)));
}

export function groupCalendarEventKindsByDate(events: CalendarEventResponse[]): Map<string, CalendarEventKind[]> {
  const grouped = new Map<string, CalendarEventKind[]>();
  for (const event of events) {
    const date = getCalendarEventDateKey(event);
    grouped.set(date, [...(grouped.get(date) ?? []), getCalendarEventKind(event)]);
  }
  return grouped;
}

export function groupCalendarEventsByDate(events: CalendarEventResponse[]): Map<string, CalendarEventResponse[]> {
  const grouped = new Map<string, CalendarEventResponse[]>();
  for (const event of events) {
    const date = getCalendarEventDateKey(event);
    grouped.set(date, [...(grouped.get(date) ?? []), event]);
  }
  return grouped;
}

export function toFullCalendarEvent(event: CalendarEventResponse): EventInput {
  const kind = getCalendarEventKind(event);
  return {
    id: getCalendarEventOccurrenceKey(event),
    title: event.title,
    start: String(event.start),
    ...(event.end !== null ? { end: String(event.end) } : {}),
    allDay: event.isAllDay,
    backgroundColor: calendarEventColors[kind],
    borderColor: calendarEventColors[kind],
    textColor: calendarEventTextColor,
    editable: false,
    extendedProps: {
      calendarEventId: event.id,
      calendarEventKind: kind,
      calendarEventTooltip: getCalendarEventTooltip(event),
      canEdit: event.canEdit,
      isSourceManaged: event.isSourceManaged,
      link: event.link,
      recurrence: event.recurrence,
    },
  };
}

export function getCalendarEventTooltip(event: CalendarEventResponse): string {
  const kind = getCalendarEventKind(event);
  const lines = [
    `Type: ${calendarEventKindLabels[kind]}`,
    `When: ${formatTooltipRange(event)}`,
  ];
  if (event.recurrence) lines.push(`Repeats: ${formatTooltipRecurrence(event.recurrence)}`);
  if (event.description?.trim()) lines.push(`Description: ${truncateTooltipText(event.description.trim())}`);
  if (event.timeZoneId) lines.push(`Event time zone: ${event.timeZoneId}`);
  lines.push(`Access: ${event.canEdit && !event.isSourceManaged ? "Editable" : "Read-only"}`);
  lines.push("Click for full details.");
  return lines.join("\n");
}

export const calendarEventLegend = [
  { kind: "custom" as const, label: calendarEventKindLabels.custom },
  { kind: "proposal" as const, label: calendarEventKindLabels.proposal },
  { kind: "workAgreement" as const, label: calendarEventKindLabels.workAgreement },
  { kind: "unknown" as const, label: calendarEventKindLabels.unknown },
];

function formatTooltipRange(event: Pick<CalendarEventResponse, "end" | "isAllDay" | "start">): string {
  if (event.isAllDay) {
    const start = parseTooltipDate(String(event.start));
    if (event.end === null) return formatTooltipDate(start);
    const end = parseTooltipDate(String(event.end));
    end.setDate(end.getDate() - 1);
    return start.getTime() === end.getTime()
      ? formatTooltipDate(start)
      : `${formatTooltipDate(start)} – ${formatTooltipDate(end)}`;
  }
  const start = formatTooltipDateTime(new Date(String(event.start)));
  return event.end === null ? start : `${start} – ${formatTooltipDateTime(new Date(String(event.end)))}`;
}

function formatTooltipRecurrence(recurrence: NonNullable<CalendarEventResponse["recurrence"]>): string {
  const units = { daily: "day", weekly: "week", monthly: "month", yearly: "year" } as const;
  const unit = units[recurrence.frequency];
  const interval = recurrence.interval === 1 ? `Every ${unit}` : `Every ${recurrence.interval} ${unit}s`;
  const weekdays = recurrence.weekdays.length > 0
    ? ` on ${recurrence.weekdays.map((weekday) => weekday
      .slice(0, calendarWeekdayAbbreviationLength)
      .replace(/^./, (value) => value.toUpperCase())).join(", ")}`
    : "";
  if (recurrence.count) return `${interval}${weekdays}, ${recurrence.count} occurrences`;
  if (recurrence.untilDate) return `${interval}${weekdays}, through ${formatTooltipDate(parseTooltipDate(recurrence.untilDate))}`;
  if (recurrence.untilUtc) return `${interval}${weekdays}, through ${formatTooltipDateTime(new Date(recurrence.untilUtc))}`;
  return `${interval}${weekdays}`;
}

function parseTooltipDate(value: string): Date {
  return new Date(`${value.slice(0, localDateInputLength)}T00:00:00`);
}

function formatTooltipDate(value: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(value);
}

function formatTooltipDateTime(value: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(value);
}

function truncateTooltipText(value: string): string {
  const ellipsis = "…";
  const visibleLength = calendarEventFieldLimits.tooltipDescription - ellipsis.length;
  return value.length <= calendarEventFieldLimits.tooltipDescription
    ? value
    : `${value.slice(0, visibleLength)}${ellipsis}`;
}
